import type { QueryPhase } from "@/hooks/useApi";
import { isLlmService } from "@/pages/Services/helpers";
import type { Service } from "@/types/api";

export function isLlmDetail(service: Service | undefined): service is Service {
  return service !== undefined && isLlmService(service);
}

// How many LLM services exist: a lone one is the model in use whatever the
// engine reports, so it can't be deleted (the Services list's rule).
export function countLlms(services: Service[] | undefined): number {
  return services?.filter(isLlmService).length ?? 0;
}

// The breadcrumb badge's verdict and lifecycle. A service is normally "live"
// when a pipeline using it is running, keyed to the jobs query. An LLM is
// never used by pipelines: it is live when it is the Agent's / AI Helper's
// current model, which the services response reports directly (see statusFor
// in the Services helpers) — so its badge never waits on (or fails with) the
// jobs query.
export function liveState(
  service: Service | undefined,
  jobsLive: boolean,
  jobsPhase: QueryPhase,
): { live: boolean; livePhase: QueryPhase } {
  if (!isLlmDetail(service)) return { live: jobsLive, livePhase: jobsPhase };
  return { live: service.is_active === true, livePhase: "ready" };
}

// The current default, a lone LLM and system services can't be deleted — the
// Services list's rule.
export function canDeleteLlm(service: Service, llmCount: number): boolean {
  return service.is_active !== true && llmCount > 1 && !service.is_system;
}
